/**
 * Константы раздела «Аналитика камеры» (Part 4, разделы 4, 6.2 и 6.9).
 *
 * Списка детекций здесь нет и быть не может: фичи приходят из detection
 * profile, и добавление helmet на бэкенде не должно требовать правок фронта.
 * Здесь живут только подписи режимов и прав.
 */

/** ТЗ раздел 4: пользователю показываем «Оборудование» и «Люди и СИЗ», а не PEOPLE/PPE. */
export const ANALYTICS_MODE_OPTIONS = [
  { value: 'equipment', label: 'Оборудование' },
  { value: 'people_ppe', label: 'Люди и СИЗ' },
];

export const getAnalyticsModeLabel = (mode) =>
  ANALYTICS_MODE_OPTIONS.find((option) => option.value === mode)?.label ?? 'Режим не определён';

/** ТЗ раздел 6.9: три режима прав, одинаковые для правил и для зон. */
export const ANALYTICS_EDIT_MODE = {
  ADMIN_ONLY: 'admin_only',
  CLIENT_WITH_APPROVAL: 'client_with_admin_approval',
  CLIENT_AND_ADMIN: 'client_and_admin',
};

export const ANALYTICS_EDIT_MODE_OPTIONS = [
  { value: ANALYTICS_EDIT_MODE.ADMIN_ONLY, label: 'Только администратор' },
  { value: ANALYTICS_EDIT_MODE.CLIENT_WITH_APPROVAL, label: 'Клиент — после одобрения администратора' },
  { value: ANALYTICS_EDIT_MODE.CLIENT_AND_ADMIN, label: 'Клиент и администратор' },
];

export const getAnalyticsEditModeLabel = (mode) =>
  ANALYTICS_EDIT_MODE_OPTIONS.find((option) => option.value === mode)?.label ?? 'Режим не задан';

/**
 * Что доступно текущему пользователю при данном режиме.
 *
 * `propose` означает «действие подаётся заявкой», а не «выполняется сразу»:
 * до одобрения администратора production-состояние не меняется (ТЗ 7.1).
 */
export const resolveEditCapability = (mode, { isAdmin }) => {
  if (isAdmin) return 'direct';
  if (mode === ANALYTICS_EDIT_MODE.CLIENT_AND_ADMIN) return 'direct';
  if (mode === ANALYTICS_EDIT_MODE.CLIENT_WITH_APPROVAL) return 'propose';
  return 'none';
};

/**
 * Человеческая расшифровка metric.required_features (ТЗ 6.1).
 *
 * Состав фич фронт не вычисляет — он приходит из каталога. Здесь только
 * подстановка display_name; неизвестный feature_id печатается как есть,
 * иначе пользователь не узнает, что правило потянет незнакомую детекцию.
 */
export const formatRequiredFeatures = (features, nameById = {}) => {
  if (!Array.isArray(features) || features.length === 0) return '';
  return features.map((featureId) => nameById[featureId] || featureId).join(', ');
};

/**
 * Типы заявок. Production C3 принимает zone_create/zone_update/zone_delete и
 * policy_upsert/policy_delete; rule_* оставлены только как подписи для старых
 * заявок в базе — подавать их фронт не предлагает. Неизвестный тип показываем
 * как есть, а не прячем.
 */
const CHANGE_TYPE_LABELS = {
  zone_create: 'Создание зоны',
  zone_update: 'Изменение зоны',
  zone_delete: 'Удаление зоны',
  policy_upsert: 'Изменение прав камеры',
  policy_delete: 'Сброс прав камеры',
  rule_create: 'Создание правила',
  rule_update: 'Изменение правила',
  rule_deactivate: 'Отключение правила',
};

export const getChangeTypeLabel = (changeType) =>
  CHANGE_TYPE_LABELS[changeType] ?? (changeType || 'Изменение');

/**
 * Статусы заявки (ТЗ 7.2). `stale` и `conflict` — не ошибки интерфейса:
 * конфигурация уехала вперёд, и заявку нельзя применять автоматически.
 */
const CHANGE_REQUEST_STATUS = {
  pending: { label: 'Ожидает одобрения', modifier: 'pending' },
  approved: { label: 'Одобрена', modifier: 'enabled' },
  rejected: { label: 'Отклонена', modifier: 'disabled' },
  stale: { label: 'Устарела', modifier: 'stale' },
  conflict: { label: 'Конфликт', modifier: 'stale' },
  cancelled: { label: 'Отменена', modifier: 'disabled' },
};

export const getChangeRequestStatus = (status) =>
  CHANGE_REQUEST_STATUS[status] ?? { label: status || 'Неизвестно', modifier: 'disabled' };

export const CHANGE_REQUEST_STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'pending', label: 'Ожидают одобрения' },
  { value: 'approved', label: 'Одобренные' },
  { value: 'rejected', label: 'Отклонённые' },
  { value: 'stale', label: 'Устаревшие' },
  { value: 'conflict', label: 'Конфликтные' },
  { value: 'cancelled', label: 'Отменённые' },
];

/** Заявок обычно немного; пагинацию добавим, если список реально вырастет. */
export const DEFAULT_CHANGE_REQUEST_LIMIT = 50;

/**
 * ТЗ задачи 21, 22: `people_legacy` — внутреннее имя транспортного
 * feature_id, пользователю оно нигде не должно попадаться на глаза. Transport
 * (сами id в payload) эта таблица не трогает — подменяется только подпись.
 */
const FEATURE_DISPLAY_OVERRIDES = { people_legacy: 'Люди' };

/** Профиль детекции присылает feature-объект целиком — есть display_name для фоллбэка. */
export const getFeatureDisplayName = (feature) => {
  if (!feature) return '';
  const id = feature.feature_id || '';
  if (FEATURE_DISPLAY_OVERRIDES[id]) return FEATURE_DISPLAY_OVERRIDES[id];
  return feature.display_name || id;
};

/**
 * Места вроде карточки заявки (ChangeRequestCard) знают только голый
 * feature_id — без display_name из профиля. Та же таблица подмен, чтобы
 * people_legacy не всплыл и там.
 */
export const getFeatureIdDisplayName = (id) => FEATURE_DISPLAY_OVERRIDES[id] || id;

/**
 * ТЗ задача 22: `people_legacy` заранее блокируется в UI (не дожидаясь 409),
 * если её держат активные правила либо камера работает в режиме «Люди и
 * СИЗ» — там детекция людей обязательна. Прочие фичи через этот хелпер не
 * блокируются: для них достаточно существующей логики чекбокса
 * (required_by_rules только снимает галочку, не отключает контрол).
 */
export const isFeatureLocked = (feature, profile) => {
  if (feature?.feature_id !== 'people_legacy') return { locked: false, reason: '' };

  if (feature.required_by_rules) {
    return { locked: true, reason: 'Детекцию людей используют активные правила — сначала деактивируйте их.' };
  }

  if (profile?.analytics_mode === 'people_ppe') {
    return { locked: true, reason: 'Детекция людей обязательна для режима «Люди и СИЗ».' };
  }

  return { locked: false, reason: '' };
};

/**
 * ТЗ 24: master-переключатель «Все детекции» — не отдельный boolean на
 * фронте, а производная от authoritative профиля детекции. Недоступные
 * (available=false) фичи не участвуют — их вообще нельзя переключить.
 */
export const deriveMasterState = (profile) => {
  const features = (profile?.features || []).filter((feature) => feature.available !== false);
  if (features.length === 0) return 'none';
  const enabled = features.filter((feature) => feature.manual_enabled).length;
  if (enabled === features.length) return 'on';
  if (enabled === 0) return 'off';
  return 'partial';
};

/**
 * ТЗ 25: включение — все доступные фичи становятся manual; выключение — manual
 * пуст, а disable_features явно перечисляет всё, что сейчас фактически
 * включено (effective_enabled), иначе backend не поймёт намерение выключить
 * фичи, которые держат активные правила.
 */
export const buildMasterPatch = (profile, on) => {
  const features = (profile?.features || []).filter((feature) => feature.available !== false);
  if (on) return { manual_features: features.map((feature) => feature.feature_id), disable_features: [] };
  return {
    manual_features: [],
    disable_features: features.filter((feature) => feature.effective_enabled).map((feature) => feature.feature_id),
  };
};
